import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  TextField,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  getEticket,
  getEticketGrouped,
  getHotelVoucher,
  getOfferDocument,
  sendDocument,
} from '../../api/endpoints/documents';
import type { DocumentKind } from '../../api/types/documents';
import type { ShoppingCart } from '../../api/types/shoppingCart';
import { ApiErrorAlert } from '../ApiErrorAlert';
import { openHtmlDocument, stripDocumentPrices } from './documentHtml';
import { buildEticketHtml } from './eticketHtml';

const KIND_LABEL: Record<DocumentKind, string> = {
  eticket: 'E-Bilet',
  'eticket-grouped': 'E-Bilet (Toplu)',
  voucher: 'Voucher',
  offer: 'Teklif / Proforma',
};

/** Fiyatlı / fiyatsız ayrımı yalnız e-bilette anlamlı; voucher / teklif tek çeşit. */
const HAS_PRICE_VARIANT = new Set<DocumentKind>(['eticket', 'eticket-grouped']);

type DocEntry = { key: string; label: string; kind: DocumentKind; noPrice: boolean };

function entriesFor(kind: DocumentKind): DocEntry[] {
  if (!HAS_PRICE_VARIANT.has(kind)) {
    return [{ key: kind, label: KIND_LABEL[kind], kind, noPrice: false }];
  }
  return [
    { key: `${kind}-priced`, label: `${KIND_LABEL[kind]} (Fiyatlı)`, kind, noPrice: false },
    { key: `${kind}-noprice`, label: `${KIND_LABEL[kind]} (Fiyatsız)`, kind, noPrice: true },
  ];
}

function fetchDocument(
  kind: DocumentKind,
  shoppingCartId: string,
  pnr?: string,
  paxReferenceId?: string,
  noPrice?: boolean,
  offerId?: string
) {
  const body = { shoppingCartId, pnr, paxReferenceId, noPrice };
  switch (kind) {
    case 'eticket':
      return getEticket(body);
    case 'eticket-grouped':
      return getEticketGrouped(body);
    case 'voucher':
      return getHotelVoucher(body);
    case 'offer':
      // `Document/Offer` bir `offerId` zorunlu kılıyor ("Teklif kimliği zorunludur.").
      // Bu genelde arama sonucundan gelir (`AirResultsList` "Teklif Oluştur"); rezervasyon
      // detay sayfasında elimizde `offerId` yoksa backend'e boşuna gitmeyip burada uyarırız.
      if (!offerId) {
        throw new Error('Teklif belgesi için bir teklif kimliği (Air/Search offerId) gerekli.');
      }
      return getOfferDocument({ offerId, shoppingCartId });
  }
}

/**
 * "Belgeler" düğmesi — bir rezervasyon (üst seviye, tüm sepet) VEYA tek bir bilet için
 * (`pnr` + `paxReferenceId` verildiğinde) belge önizleme / yazdırma / e-posta.
 *
 * E-bilet iki çeşit sunulur: **Fiyatlı** ve **Fiyatsız**. Backend fiyatsız bayrağını henüz
 * okumadığından (`docs/backend-requirements.md` N-11) fiyatsız çeşitte fiyat sütunları
 * dönen HTML'den `stripDocumentPrices` ile ayıklanır (`noPrice` yine de gönderilir ki
 * backend desteklediğinde kendiliğinden düzgün gelsin).
 *
 * API HTML döndürür (`{ html }`, PDF değil) — önizleme yeni sekmede açar, yazdır ayrıca
 * tarayıcı yazdırma penceresini tetikler. `Document/Send` yalnız e-posta kanalını bağlar.
 */
export function DocumentMenu({
  shoppingCartId,
  kinds,
  pnr,
  paxReferenceId,
  offerId,
  cart,
  label = 'Belgeler',
  buttonSize = 'small',
}: {
  shoppingCartId: string;
  kinds: DocumentKind[];
  pnr?: string;
  paxReferenceId?: string;
  /** `Document/Offer` için zorunlu teklif kimliği — genelde `AirSearchResponse.offerId`.
   *  Verilmezse "Teklif / Proforma" menü öğesi tıklanınca uyarı gösterir. */
  offerId?: string;
  /** Verildiğinde e-bilet önizleme/yazdırma bu sepetten zengin HTML üretilir (logo +
   *  uçuş bilgisi) — API çağrısına gerek kalmadan. */
  cart?: ShoppingCart | null;
  label?: string;
  buttonSize?: 'small' | 'medium' | 'large';
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [sendEntry, setSendEntry] = useState<DocEntry | null>(null);
  const [to, setTo] = useState('');

  /** E-bilet çeşitleri için elimizde sepet varsa zengin HTML'i yerelde üret; voucher /
   *  teklif ve sepet yoksa API'ye düş. */
  const canBuildLocally = (kind: DocumentKind) =>
    !!cart && (kind === 'eticket' || kind === 'eticket-grouped');

  const viewMutation = useMutation({
    mutationFn: async ({ entry, print }: { entry: DocEntry; print: boolean }) => {
      let html: string | null | undefined;
      if (canBuildLocally(entry.kind)) {
        html = buildEticketHtml(cart, {
          pnr,
          paxReferenceId,
          noPrice: entry.noPrice,
          grouped: entry.kind === 'eticket-grouped',
        });
      } else {
        const res = await fetchDocument(entry.kind, shoppingCartId, pnr, paxReferenceId, entry.noPrice, offerId);
        html = res.html;
        // Backend fiyatsız bayrağını okumuyor → dönen HTML'den ayıkla.
        if (html && entry.noPrice) html = stripDocumentPrices(html);
      }
      return { html, print };
    },
    onSuccess: ({ html, print }) => {
      if (html) openHtmlDocument(html, print);
      else enqueueSnackbar('Belge içeriği alınamadı.', { variant: 'warning' });
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Belge alınamadı.', { variant: 'error' }),
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      sendDocument({
        shoppingCartId,
        documentType: sendEntry!.kind,
        channel: 'email',
        to,
        pnr,
        paxReferenceId,
        noPrice: sendEntry!.noPrice,
      }),
    onSuccess: (res) => {
      enqueueSnackbar(res.successMessage ?? 'E-posta gönderildi.', { variant: 'success' });
      setSendEntry(null);
      setTo('');
    },
    onError: (err) =>
      enqueueSnackbar(err instanceof Error ? err.message : 'Gönderim başarısız.', { variant: 'error' }),
  });

  if (kinds.length === 0) return null;

  const entries: DocEntry[] = kinds.flatMap((kind) => entriesFor(kind));
  const menuItems: React.ReactNode[] = [];
  entries.forEach((entry, idx) => {
    if (idx > 0) menuItems.push(<Divider key={`${entry.key}-sep`} />);
    menuItems.push(
      <MenuItem
        key={`${entry.key}-preview`}
        onClick={() => {
          setAnchor(null);
          viewMutation.mutate({ entry, print: false });
        }}
      >
        {entry.label} — Önizle
      </MenuItem>,
      <MenuItem
        key={`${entry.key}-print`}
        onClick={() => {
          setAnchor(null);
          viewMutation.mutate({ entry, print: true });
        }}
      >
        {entry.label} — Yazdır
      </MenuItem>
    );
    if (entry.kind !== 'offer') {
      menuItems.push(
        <MenuItem
          key={`${entry.key}-email`}
          onClick={() => {
            setAnchor(null);
            setSendEntry(entry);
          }}
        >
          {entry.label} — E-posta Gönder
        </MenuItem>
      );
    }
  });

  return (
    <>
      <Button
        size={buttonSize}
        variant="outlined"
        startIcon={<DescriptionOutlinedIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={viewMutation.isPending}
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {menuItems}
      </Menu>

      <Dialog open={!!sendEntry} onClose={() => setSendEntry(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{sendEntry ? `${sendEntry.label} — E-posta` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              type="email"
              label="E-posta adresi"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <ApiErrorAlert error={sendMutation.error} sx={{ mt: 2 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEntry(null)} disabled={sendMutation.isPending}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            onClick={() => sendMutation.mutate()}
            disabled={!to || sendMutation.isPending}
          >
            {sendMutation.isPending ? 'Gönderiliyor…' : 'Gönder'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
